<template>
  <section class="float-left ml-3 card-chat-booking" v-if="serviceRequest && order">
    <!-- Main content of service request -->
    <div class="card">
      <div class="card-body">
        <b>{{ message.text }}</b>
        <div class="alert alert-info alert-dismissible fade show mt-2" role="alert"
             v-if="serviceRequest.payment_status === 1">
          {{ $t('message.service_request.service_request_remaining_balance_notification') }}
          <button type="button" class="close" data-dismiss="alert" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <hr>
        <section>
          <div class="mb-3">
            #{{ order.order_code }} - <span
            v-html="$options.filters.serviceParentName(order.service_id, globalState.services)"></span>
          </div>
          <hr>
          <!-- Status -->
          <section>
            <div>
              <label>{{ $t('message.service_request.status') }}</label>
            </div>
            <div>
              <i class="fas fa-circle fa-sm -status-pending"></i>&nbsp;
              {{ serviceRequest.service_request_status | multiLangName }}
            </div>
          </section>
          <hr>
          <!-- Service date -->
          <section>
            <div>
              <label>{{ $t('message.service_request.service_date') }}</label>
            </div>
            <div>
            <span v-if="serviceRequest.is_confirmed === 0">
              {{ serviceRequest.service_date | prettyDate }}
            </span>
              <span v-else>
              <span v-if="serviceRequest.actual_service_date">
                {{ serviceRequest.actual_service_date | prettyDate }}
              </span>
              <span v-else>
                {{ serviceRequest.offered_service_date | prettyDate }}
              </span>
            </span>
            </div>
            <div class="pt-2" v-if="serviceRequest.offered_service_date && serviceRequest.is_confirmed === 0">
              <div>
                <label>{{ $t('message.service_request.service_request_provider_offered_service_date') }}</label>
              </div>
              <div class="-status-new">
                {{ serviceRequest.offered_service_date | prettyDate }}
              </div>
            </div>
            <div class="pt-2" v-if="!serviceRequest.offered_service_date && serviceRequest.is_confirmed === 0">
              <div class="text-remark">
                <i class="fas fa-circle fa-sm -status-pending"></i>&nbsp;
                {{ $t('message.service_request.service_request_service_date_remark') }}
              </div>
            </div>
            <div class="pt-2" v-if="serviceRequest.duration_remark">
              <div>
                <label>{{ $t('message.service_request.service_request_duration_remark') }}</label>
              </div>
              <div>
                {{ serviceRequest.duration_remark }}
              </div>
            </div>
          </section>
          <!-- Professional -->
          <section v-if="order.professional">
            <hr>
            <div>
              <label>{{ $t('message.my_reservations.cleaner_name') }}</label>
            </div>
            <div class="d-flex justify-content-start align-items-center">
              <div>
                <img :src="order.professional.full_picture" alt="" class="rounded-circle" :width="$mq.phone ? 48 : 52">
              </div>
              <div class="pl-2">
                {{ order.professional.full_name | firstName }}
              </div>
            </div>
          </section>
          <!-- Purpose -->
          <section v-if="serviceRequest.service_request_purpose">
            <hr>
            <div>
              <label>{{ $t('message.service_request.purpose') }}</label>
            </div>
            <div>
              {{ serviceRequest.service_request_purpose | multiLangName }}
            </div>
          </section>
          <hr>
          <!-- Building type & size -->
          <section>
            <div>
              <label v-if="serviceRequest.building_type">
                {{ $t('message.service_request.building_type') }}
              </label>
              <label v-else>
                {{ $t('message.service_request.furniture_detail') }}
              </label>
            </div>
            <div>
              <span v-if="serviceRequest.building_type">
                {{ serviceRequest.building_type | multiLangName }} - {{ serviceRequest.building_size }}
              </span>
              <span v-else>
                {{ serviceRequest.building_size }}
              </span>
            </div>
          </section>
          <hr>
          <!-- Building address -->
          <section>
            <div>
              <label>{{ $t('message.service_request.address') }}</label>
            </div>
            <div>
              {{ serviceRequest.building_address }}
              <span v-if="serviceRequest.sub_district">
              {{ serviceRequest.sub_district | multiLangName }}
            </span>
              {{ serviceRequest.district | multiLangName }}
              {{ serviceRequest.address_province | multiLangName }}
            </div>
          </section>
          <hr>
          <section>
            <div>
              <label>{{ $t('message.service_request.remark') }}</label>
            </div>
            <div>
              {{ serviceRequest.remark }}
            </div>
          </section>
          <!-- Photo -->
          <section v-if="serviceRequest.service_request_photos.length > 0">
            <hr>
            <label>
              <i class="fal fa-images"></i> {{ $t('message.service_request.photos') }}
            </label>
            <photos-album :photos="serviceRequest.service_request_photos"
                          :photoType="'service_request_photos'"
                          :includeGallery="false"
                          @onItemClick="setGalleryIndex"/>
          </section>

          <!-- Service cost -->
          <section v-if="serviceRequest.service_cost">
            <hr>
            <div class="row no-gutters">
              <div class="col-6">
                <label>{{ $t('message.service_request.service_cost') }}</label>
              </div>
              <div class="col-6 text-right">
                <label>{{ serviceRequest.service_cost | currency }}</label>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-6">
                {{ $t('message.service_request.service_request_payment_status') }}
              </div>
              <div class="col-6 text-right">
                <span :class="[(serviceRequest.payment_status === 2) ? '-status-new' : 'text-orange']">
                  {{ serviceRequest.payment_status | serviceRequestPaymentStatus }}
                </span>
              </div>
            </div>

            <!--  Remaining balance  -->
            <div class="row no-gutters mt-3" v-if="serviceRequest.payment_status === 1">
              <div class="col-6">
                <label>{{ $t('message.service_request.service_request_remaining_balance') }}</label>
              </div>
              <div class="col-6 text-right" style="font-weight: bold;">
                <label>{{ parseFloat((serviceRequest.service_cost / 2)).toFixed(2) | currency }}</label>
              </div>
              <div class="col-12 text-remark">
                <i class="fas fa-circle fa-sm -status-pending"></i>
                {{ $t('message.service_request.service_request_remaining_balance_remark') }}
              </div>
            </div>
          </section>

          <!--  Service request payments  -->
          <template v-if="serviceRequest.service_request_payments.length > 0">
            <hr class="my-4">
            <div class="row no-gutters my-3">
              <div class="col-12 text-remark">
                {{ $t('message.booking.payment_step.payment') }}
              </div>
            </div>

            <div v-for="(payment, index) in serviceRequest.service_request_payments" :key="payment.id">
              <!--  ประเภท  -->
              <div class="row no-gutters">
                <div class="col-6">
                  {{ $t('message.service_request.payment_type') }}
                </div>
                <div class="col-6 text-right">
                  {{ payment.payment_type | serviceRequestPaymentType }}
                </div>
              </div>
              <!--  ชำระเมื่อ -->
              <div class="row no-gutters mt-2">
                <div class="col-6">
                  {{ $t('message.service_request.service_request_paid_on') }}
                </div>
                <div class="col-6 text-right">
                  {{ payment.created_at | prettyDate }}
                </div>
              </div>
              <!--  สถานะ  -->
              <div class="row no-gutters mt-2">
                <div class="col-6">
                  {{ $t('message.service_request.status') }}
                </div>
                <div class="col-6 text-right">
                  <span class="-status-new" v-if="payment.status === 1">
                    {{ $t('message.my_reservations.already_paid') }}
                  </span>
                  <span class="text-orange" v-else>
                    {{ $t('message.service_request.order_status_waiting_confirm') }}
                  </span>
                </div>
              </div>
              <!-- total -->
              <div class="row no-gutters mt-2">
                <div class="col-6">
                  <label>{{ $t('message.service_request.all_total_cost') }}</label>
                </div>
                <div class="col-6 text-right">
                  <label>{{ getTotalCost(payment) | currency }}</label>
                </div>
              </div>
              <!-- Paid by -->
              <div class="row no-gutters">
                <div class="col-12">
                  <span v-html="$options.filters.orderPaymentData(payment.payment_data)"></span>
                </div>
              </div>
              <hr v-if="index+1 !== _.size(serviceRequest.service_request_payments)">
            </div>
          </template>

        </section><!-- End Main Content -->
        <hr>
        <section>
          <button class="btn btn-outline-info btn-block" @click="backToMainMenu()">กลับสู่เมนูหลัก</button>
        </section>
      </div>
    </div>

    <!-- ส่งราคามาให้ลูกค้าแล้ว รอให้ลูกค้ากดยืนยัน หรือ ปฏิเสธ   -->
    <confirm-offer-section :serviceRequest="serviceRequest"
                           :serviceRequestLostSalesReasons="serviceRequestLostSalesReasons"
                           @confirmDialogAction="confirmDialogAction()"/>
    <!-- Payment section -->
    <payment-section :serviceRequest="serviceRequest" @openPaymentDialog="openPaymentDialog()"/>
    <!-- Payment modal -->
    <payment-modal @fetchData="getServiceRequest"
                   :order="order"
                   :serviceRequest="serviceRequest"
                   ref="paymentModal"/>
  </section>
</template>

<script>
  import { mapGetters } from 'vuex'
  import OrderSummaryTotal from '../../order/components/OrderSummaryTotal'
  import ProfessionalPicture from '../../professional/components/ProfessionalPicture'
  import PhotosAlbum from '../../order/components/PhotosAlbum'
  import ConfirmOfferSection from '../../serviceRequest/Components/ConfirmOfferSection'
  import PaymentSection from '../../serviceRequest/Components/PaymentSection'
  import PaymentModal from '../../serviceRequest/Components/PaymentModal'
  import axios from 'axios'

  export default {
    props: {
      message: Object
    },
    components: {
      OrderSummaryTotal,
      ProfessionalPicture,
      PhotosAlbum,
      ConfirmOfferSection,
      PaymentSection,
      PaymentModal
    },
    data () {
      return {
        serviceRequest: null,
        serviceRequestStates: null,
        serviceRequestLostSalesReasons: null,
        professionalQuotation: null,
        order: null
      }
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState',
        user: 'auth/getUser'
      })
    },
    methods: {
      backToMainMenu () {
        const action = {
          label: 'main-menu',
          text: 'กลับสู่เมนูหลัก'
        }
        this.sendMessage(action)
      },
      sendMessage (action) {
        const message = {
          sender_type: 'user',
          type: 'text',
          label: action.label,
          text: action.text,
          data: action.data
        }
        this.$store.commit('chatBooking/ADD_MESSAGE', message)
      },
      setGalleryIndex (index, type) {
        const images = this._.map(this.serviceRequest.service_request_photos, 'image_xl')
        this.$parent.$parent.openGallery(images, index)
      },
      confirmDialogAction (result) {
        this.getServiceRequest()
      },
      openPaymentDialog () {
        this.$refs.paymentModal.openModal(this.serviceRequest.id)
      },
      async getServiceRequest () {
        const url = `/service-requests/${this.serviceRequest.id}`
        let {data} = await axios.get(url)
        if (!data.error) {
          this.serviceRequest = data.service_request
          this.serviceRequestStates = data.service_request_states
          this.order = data.order
          this.professionalQuotation = data.professional_quotation
        } else {
          this.$swal({text: 'ผิดพลาด ' + data.message, type: 'error'})
        }
      },
      getTotalCost (payment = null) {
        let calculate = Number(payment.total_price) + Number(payment.service_vat)
        return calculate.toFixed(2)
      }
    },
    mounted () {
      const response = this.message.data
      this.serviceRequest = response.service_request
      this.serviceRequestStates = response.service_request_states
      this.serviceRequestLostSalesReasons = response.service_request_lost_sales_reasons
      this.order = response.order
      this.professionalQuotation = response.professional_quotation
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/chatBooking/components/ServiceRequestDetailMessage.vue